"use strict";

import * as devConsole from "./devConsole";
import {
  clearV3OfflineUrl,
  forceOfflineUrlIfEnabled,
  readStoredForceOfflineMode,
  writeStoredForceOfflineMode,
} from "./forceOffline";
import notify from "./notify";

export enum BhopMode {
  DISABLED = 0,
  SLIDEHOP = 1,
  HOLD_JUMP_CROUCH = 2,
  ALWAYS_JUMP = 3,
  HOLD_JUMP = 5,
}

const LEGACY_HOLD_JUMP_CROUCH_MODE = 4 as const;
export const BHOP_MODE_ORDER: BhopMode[] = [
  BhopMode.DISABLED,
  BhopMode.SLIDEHOP,
  BhopMode.HOLD_JUMP_CROUCH,
  BhopMode.ALWAYS_JUMP,
  BhopMode.HOLD_JUMP,
];
const isBhopMode = (value: number): value is BhopMode =>
  BHOP_MODE_ORDER.includes(value as BhopMode);

window.bhopMode = Number(localStorage.getItem("bhop"));
if (window.bhopMode === LEGACY_HOLD_JUMP_CROUCH_MODE) {
  window.bhopMode = BhopMode.HOLD_JUMP_CROUCH;
  localStorage.setItem("bhop", String(window.bhopMode));
}
if (
  !Number.isFinite(window.bhopMode) ||
  !isBhopMode(window.bhopMode)
)
  window.bhopMode = BhopMode.DISABLED;
window.bhopEnabled = window.bhopMode !== BhopMode.DISABLED;
window.bhop = false;
export const BHOP_MODE_NAMES: Record<BhopMode, string> = {
  [BhopMode.DISABLED]: "Disabled",
  [BhopMode.SLIDEHOP]: "Slidehop (while moving)",
  [BhopMode.HOLD_JUMP_CROUCH]: "Hold Jump/Crouch",
  [BhopMode.ALWAYS_JUMP]: "Always Jump (while moving)",
  [BhopMode.HOLD_JUMP]: "Hold Jump",
};
window.ctrlTpEnabled = localStorage.getItem("ctrlTp") === "1";
window.ctrlTp = false;
window.forceOfflineMode = readStoredForceOfflineMode();

function readStoredValue(key: string): string | boolean | null {
  if (typeof window.getSavedVal === "function") {
    return window.getSavedVal(key);
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredValue(key: string, value: string): void {
  if (typeof window.saveVal === "function") {
    window.saveVal(key, value);
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function readStoredBool(key: string, fallback: boolean): boolean {
  const value = readStoredValue(key);
  if (value == null) return fallback;
  return value === true || value === "true" || value === "1";
}

function formatKeyBind(code: string): string {
  if (!code) return "Unbound";
  const special: Record<string, string> = {
    Backquote: "`",
    Minus: "-",
    Equal: "=",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Semicolon: ";",
    Quote: "'",
    Comma: ",",
    Period: ".",
    Slash: "/",
    Space: "Space",
    Escape: "Esc",
    ArrowUp: "Up",
    ArrowDown: "Down",
    ArrowLeft: "Left",
    ArrowRight: "Right",
  };
  if (special[code]) return special[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `Num ${code.slice(6)}`;
  return code
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/(Left|Right)$/, " $1");
}

window.devConsoleEnabled = readStoredBool(
  "kro_setngss_devConsoleEnabled",
  true, // ["127.0.0.1", "localhost"].includes(location.hostname),
);
{
  const storedBind = readStoredValue("krk_dev_console_bind");
  window.devConsoleKey = typeof storedBind === "string" ? storedBind : "Backquote";
}
{
  const storedBind = readStoredValue("krk_cycle_command_bind");
  window.cycleCommandKey = typeof storedBind === "string" ? storedBind : "F8";
}
{
  const storedBind = readStoredValue("krk_bhop_mode_bind");
  window.bhopModeKey = typeof storedBind === "string" ? storedBind : "KeyB";
}

let devConsoleCapturingKey = false;
let cycleCommandCapturingKey = false;
let bhopModeCapturingKey = false;

function refreshDevConsoleUi(): void {
  const offlineInput = document.querySelector<HTMLInputElement>(
    "[data-force-offline-setting] input[type='checkbox']",
  );
  if (offlineInput) offlineInput.checked = !!window.forceOfflineMode;

  const settingInput = document.querySelector<HTMLInputElement>(
    "[data-dev-console-setting] input[type='checkbox']",
  );
  if (settingInput) settingInput.checked = !!window.devConsoleEnabled;

  const bindValue = document.querySelector(
    "[data-dev-console-bind] .settText.floatRNoC.keyIcon",
  );
  if (bindValue) {
    bindValue.textContent = devConsoleCapturingKey
      ? "Press any Key"
      : formatKeyBind(window.devConsoleKey);
  }

  const cycleBindValue = document.querySelector(
    "[data-cycle-command-bind] .settText.floatRNoC.keyIcon",
  );
  if (cycleBindValue) {
    cycleBindValue.textContent = cycleCommandCapturingKey
      ? "Press any Key"
      : formatKeyBind(window.cycleCommandKey);
  }

  const bhopBindValue = document.querySelector(
    "[data-bhop-mode-bind] .settText.floatRNoC.keyIcon",
  );
  if (bhopBindValue) {
    bhopBindValue.textContent = bhopModeCapturingKey
      ? "Press any Key"
      : formatKeyBind(window.bhopModeKey);
  }
}

function setForceOfflineMode(value: boolean): void {
  window.forceOfflineMode = !!value;
  writeStoredForceOfflineMode(window.forceOfflineMode);
  if (window.forceOfflineMode) {
    forceOfflineUrlIfEnabled();
  } else {
    clearV3OfflineUrl();
  }
  refreshDevConsoleUi();
}

function setDevConsoleEnabled(value: boolean): void {
  window.devConsoleEnabled = !!value;
  writeStoredValue(
    "kro_setngss_devConsoleEnabled",
    String(window.devConsoleEnabled),
  );
  if (!window.devConsoleEnabled && devConsole.visible) {
    instructionHolder?.click?.();
    devConsole.toggleConsole();
  }
  refreshDevConsoleUi();
}

function setDevConsoleBind(code: string): void {
  window.devConsoleKey = code || "";
  writeStoredValue("krk_dev_console_bind", window.devConsoleKey);
  refreshDevConsoleUi();
}

function setCycleCommandBind(code: string): void {
  window.cycleCommandKey = code || "";
  writeStoredValue("krk_cycle_command_bind", window.cycleCommandKey);
  refreshDevConsoleUi();
}

function setBhopModeBind(code: string): void {
  window.bhopModeKey = code || "";
  writeStoredValue("krk_bhop_mode_bind", window.bhopModeKey);
  refreshDevConsoleUi();
}

function matchesDevConsoleBind(event: KeyboardEvent): boolean {
  return (
    !!window.devConsoleEnabled &&
    !!window.devConsoleKey &&
    event.code === window.devConsoleKey
  );
}

function matchesCycleCommandBind(event: KeyboardEvent): boolean {
  return !!window.cycleCommandKey && event.code === window.cycleCommandKey;
}

function matchesBhopModeBind(event: KeyboardEvent): boolean {
  return !!window.bhopModeKey && event.code === window.bhopModeKey;
}

function toggleBhopMode(): void {
  {
    const currentMode = isBhopMode(window.bhopMode)
      ? window.bhopMode
      : BhopMode.DISABLED;
    const modeIndex = BHOP_MODE_ORDER.indexOf(currentMode);
    window.bhopMode =
      BHOP_MODE_ORDER[(modeIndex + 1) % BHOP_MODE_ORDER.length];
  }
  window.bhopEnabled = window.bhopMode !== BhopMode.DISABLED;
  localStorage.setItem("bhop", String(window.bhopMode));
  {
    const msg =
      "Bhop - " +
      BHOP_MODE_NAMES[
        isBhopMode(window.bhopMode) ? window.bhopMode : BhopMode.DISABLED
      ];
    // io_ch(null, msg, 2);
    notify(msg);
    console.log(msg);
  }
}

function toggleDeveloperConsole(): void {
  if (!window.devConsoleEnabled) return;
  if (devConsole.visible) instructionHolder?.click?.();
  else document.exitPointerLock?.();
  devConsole.toggleConsole();
}

const PLAYER_LIST_WINDOW_ID = 23;
let lastPlayerListAltKeydownAt = 0;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  );
}

function isPlayerListKey(event: KeyboardEvent): boolean {
  return (
    event.key === "Alt" ||
    event.code === "AltLeft" ||
    event.code === "AltRight"
  );
}

function getElement(id: string): HTMLElement | null {
  const el = document.getElementById(id);
  return el instanceof HTMLElement ? el : null;
}

function isPlayerListWindowOpen(): boolean {
  const holder = getElement("windowHolder");
  if (!holder || holder.style.display !== "block") return false;

  const header = getElement("windowHeader")?.textContent?.trim();
  const expectedHeader =
    window.windows?.[PLAYER_LIST_WINDOW_ID - 1]?.header?.trim();
  return !!header && !!expectedHeader && header === expectedHeader;
}

function closePlayerListWindow(returnToGame = false): void {
  if (!isPlayerListWindowOpen()) return;

  const showWindow = (window as unknown as {
    showWindow?: (windowId: number, forceRefresh?: boolean) => void;
  }).showWindow;
  if (typeof showWindow !== "function") return;

  showWindow(PLAYER_LIST_WINDOW_ID);

  if (!returnToGame) return;
  window.setTimeout(() => {
    CONTROLS.resetKeys?.();
    CONTROLS.toggle?.(true);
  }, 0);
}

document.addEventListener(
  "keydown",
  (e) => {
    if (
      !devConsole.visible &&
      !isEditableTarget(e.target) &&
      isPlayerListKey(e)
    ) {
      lastPlayerListAltKeydownAt = Date.now();
    }

    if (e.key !== "Escape" || e.repeat || devConsole.visible) return;
    if (!isPlayerListWindowOpen()) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    closePlayerListWindow(true);
  },
  true,
);

window.addEventListener("blur", () => {
  if (Date.now() - lastPlayerListAltKeydownAt > 1500) return;
  window.setTimeout(() => closePlayerListWindow(false), 0);
});

function beginDevConsoleBindCapture(): void {
  if (devConsoleCapturingKey || cycleCommandCapturingKey || bhopModeCapturingKey)
    return;
  devConsoleCapturingKey = true;
  refreshDevConsoleUi();

  const captureKey = (event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    document.removeEventListener("keydown", captureKey, true);
    devConsoleCapturingKey = false;
    if (event.code !== "Escape") {
      setDevConsoleBind(event.code);
    } else {
      refreshDevConsoleUi();
    }
  };

  document.addEventListener("keydown", captureKey, true);
}

function beginCycleCommandBindCapture(): void {
  if (devConsoleCapturingKey || cycleCommandCapturingKey || bhopModeCapturingKey)
    return;
  cycleCommandCapturingKey = true;
  refreshDevConsoleUi();

  const captureKey = (event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    document.removeEventListener("keydown", captureKey, true);
    cycleCommandCapturingKey = false;
    if (event.code !== "Escape") {
      setCycleCommandBind(event.code);
    } else {
      refreshDevConsoleUi();
    }
  };

  document.addEventListener("keydown", captureKey, true);
}

function beginBhopModeBindCapture(): void {
  if (devConsoleCapturingKey || cycleCommandCapturingKey || bhopModeCapturingKey)
    return;
  bhopModeCapturingKey = true;
  refreshDevConsoleUi();

  const captureKey = (event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    document.removeEventListener("keydown", captureKey, true);
    bhopModeCapturingKey = false;
    if (event.code !== "Escape") {
      setBhopModeBind(event.code);
    } else {
      refreshDevConsoleUi();
    }
  };

  document.addEventListener("keydown", captureKey, true);
}

function makeDevConsoleSettingRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.devConsoleSetting = "1";
  row.title = "Allows opening the developer console in-game";
  row.append(document.createTextNode("Enable Developer Console (`) "));

  const label = document.createElement("label");
  label.className = "switch";
  label.style.marginLeft = "10px";

  const input = document.createElement("input");
  input.type = "checkbox";
  input.checked = !!window.devConsoleEnabled;
  input.addEventListener("click", () => setDevConsoleEnabled(input.checked));

  const slider = document.createElement("span");
  slider.className = "slider";

  label.append(input, slider);
  row.append(label);
  return row;
}

function makeForceOfflineSettingRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.forceOfflineSetting = "1";
  row.title = "Starts the game directly in offline test mode.";
  row.append(document.createTextNode("Force Offline Mode "));

  const label = document.createElement("label");
  label.className = "switch";
  label.style.marginLeft = "10px";

  const input = document.createElement("input");
  input.type = "checkbox";
  input.checked = !!window.forceOfflineMode;
  input.addEventListener("click", () => setForceOfflineMode(input.checked));

  const slider = document.createElement("span");
  slider.className = "slider";

  label.append(input, slider);
  row.append(label);
  return row;
}

function makeDevConsoleBindHeading(): HTMLDivElement {
  const heading = document.createElement("div");
  heading.className = "setHedBind";
  heading.dataset.devConsoleBindHead = "1";
  heading.textContent = "Developer Tools";
  return heading;
}

function makeDevConsoleBindRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.devConsoleBind = "1";
  row.append(document.createTextNode("Developer Console"));

  const clear = document.createElement("span");
  clear.className = "unbind";
  clear.addEventListener("click", () => setDevConsoleBind(""));

  const clearIcon = document.createElement("i");
  clearIcon.className = "material-icons";
  clearIcon.style.fontSize = "40px";
  clearIcon.style.color = "var(--red)";
  clearIcon.textContent = "delete_forever";
  clear.append(clearIcon);

  const bind = document.createElement("span");
  bind.className = "settText floatRNoC keyIcon";
  bind.textContent = formatKeyBind(window.devConsoleKey);
  bind.addEventListener("mouseover", () => window.playTick?.());
  bind.addEventListener("click", beginDevConsoleBindCapture);

  row.append(clear, bind);
  return row;
}

function makeCycleCommandBindRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.cycleCommandBind = "1";
  row.append(document.createTextNode("Cycle Map (/cycle)"));

  const clear = document.createElement("span");
  clear.className = "unbind";
  clear.addEventListener("click", () => setCycleCommandBind(""));

  const clearIcon = document.createElement("i");
  clearIcon.className = "material-icons";
  clearIcon.style.fontSize = "40px";
  clearIcon.style.color = "var(--red)";
  clearIcon.textContent = "delete_forever";
  clear.append(clearIcon);

  const bind = document.createElement("span");
  bind.className = "settText floatRNoC keyIcon";
  bind.textContent = formatKeyBind(window.cycleCommandKey);
  bind.addEventListener("mouseover", () => window.playTick?.());
  bind.addEventListener("click", beginCycleCommandBindCapture);

  row.append(clear, bind);
  return row;
}

function makeBhopModeBindRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.bhopModeBind = "1";
  row.append(document.createTextNode("Bhop Mode"));

  const clear = document.createElement("span");
  clear.className = "unbind";
  clear.addEventListener("click", () => setBhopModeBind(""));

  const clearIcon = document.createElement("i");
  clearIcon.className = "material-icons";
  clearIcon.style.fontSize = "40px";
  clearIcon.style.color = "var(--red)";
  clearIcon.textContent = "delete_forever";
  clear.append(clearIcon);

  const bind = document.createElement("span");
  bind.className = "settText floatRNoC keyIcon";
  bind.textContent = formatKeyBind(window.bhopModeKey);
  bind.addEventListener("mouseover", () => window.playTick?.());
  bind.addEventListener("click", beginBhopModeBindCapture);

  row.append(clear, bind);
  return row;
}

function ensureDevConsoleSettingRow(): void {
  const anchorInput = document.getElementById("slid_flickClamp");
  if (!anchorInput) return;
  const anchorRow = anchorInput.closest(".settName");
  if (!anchorRow) return;

  let offlineRow = document.querySelector("[data-force-offline-setting='1']");
  if (!offlineRow) {
    offlineRow = makeForceOfflineSettingRow();
    anchorRow.insertAdjacentElement("afterend", offlineRow);
  } else if (offlineRow.previousElementSibling !== anchorRow) {
    offlineRow.remove();
    anchorRow.insertAdjacentElement("afterend", offlineRow);
  }

  let row = document.querySelector("[data-dev-console-setting='1']");
  if (!row) {
    row = makeDevConsoleSettingRow();
    offlineRow.insertAdjacentElement("afterend", row);
  } else if (row.previousElementSibling !== offlineRow) {
    row.remove();
    offlineRow.insertAdjacentElement("afterend", row);
  }

  refreshDevConsoleUi();
}

function ensureDevConsoleBindRow(): void {
  const anchor =
    document.getElementById("cont37") || document.getElementById("cont34");
  if (!anchor) return;
  const anchorRow = anchor.closest(".settName");
  if (!anchorRow) return;

  let heading = document.querySelector("[data-dev-console-bind-head='1']");
  let devConsoleRow = document.querySelector("[data-dev-console-bind='1']");
  let bhopModeRow = document.querySelector("[data-bhop-mode-bind='1']");
  let cycleCommandRow = document.querySelector("[data-cycle-command-bind='1']");
  if (!heading || !devConsoleRow || !bhopModeRow || !cycleCommandRow) {
    heading?.remove();
    devConsoleRow?.remove();
    bhopModeRow?.remove();
    cycleCommandRow?.remove();
    heading = makeDevConsoleBindHeading();
    devConsoleRow = makeDevConsoleBindRow();
    bhopModeRow = makeBhopModeBindRow();
    cycleCommandRow = makeCycleCommandBindRow();
    anchorRow.insertAdjacentElement("afterend", heading);
    heading.insertAdjacentElement("afterend", devConsoleRow);
    devConsoleRow.insertAdjacentElement("afterend", bhopModeRow);
    bhopModeRow.insertAdjacentElement("afterend", cycleCommandRow);
  } else if (
    heading.previousElementSibling !== anchorRow ||
    devConsoleRow.previousElementSibling !== heading ||
    bhopModeRow.previousElementSibling !== devConsoleRow ||
    cycleCommandRow.previousElementSibling !== bhopModeRow
  ) {
    heading.remove();
    devConsoleRow.remove();
    bhopModeRow.remove();
    cycleCommandRow.remove();
    anchorRow.insertAdjacentElement("afterend", heading);
    heading.insertAdjacentElement("afterend", devConsoleRow);
    devConsoleRow.insertAdjacentElement("afterend", bhopModeRow);
    bhopModeRow.insertAdjacentElement("afterend", cycleCommandRow);
  }

  refreshDevConsoleUi();
}

function syncDevConsoleInterface(): void {
  ensureDevConsoleSettingRow();
  ensureDevConsoleBindRow();
}

{
  let scheduled = false;
  const scheduleSync = (): void => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      syncDevConsoleInterface();
    });
  };

  const startObserver = (): void => {
    const root = document.body || document.documentElement;
    if (!root) {
      requestAnimationFrame(startObserver);
      return;
    }
    new MutationObserver(scheduleSync).observe(root, {
      childList: true,
      subtree: true,
    });
    scheduleSync();
  };

  startObserver();
}

document.addEventListener("keydown", (e) => {
  // Ignore OS auto-repeat so holding a key doesn't spam actions (e.g. ` cycling maps).
  if (e.repeat) return;
  const target = e.target as Element | null;
  const input = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA";

  if (matchesDevConsoleBind(e)) {
    // Allow the bind to close the console even when its own input is focused.
    if (input && !devConsole.visible) return;
    e.preventDefault();
    toggleDeveloperConsole();
    return;
  }

  if (!input && !devConsole.visible && matchesCycleCommandBind(e)) {
    e.preventDefault();
    window.io.send("cycle");
    return;
  }

  if (!input && !devConsole.visible && matchesBhopModeBind(e)) {
    e.preventDefault();
    toggleBhopMode();
    return;
  }

  switch (e.key) {
    case "F1":
      if (input) return;
      window.io.send("clearLog");
      console.clear();
      break;
    case "F4":
      if (input) return;
      location.href = "/";
      break;
    case "F5":
      location.reload();
      break;
    case "F6":
      console.log("Creating editor window");
      // ipcRenderer.send("create-editor");
      window.open("editor.html");
      break;
    case "T":
    case "t":
      if (input) return;
      window.ctrlTpEnabled = !window.ctrlTpEnabled;
      localStorage.setItem("ctrlTp", String(+window.ctrlTpEnabled));
      {
        const msg =
          "Ctrl TP - " + (window.ctrlTpEnabled ? "Enabled" : "Disabled");
        // io_ch(null, msg, 2);
        notify(msg);
        console.log(msg);
      }
      break;
  }
});

{
  const pr = Array.prototype;
  const { push } = pr;
  pr.push = function (this: unknown[], ...args: unknown[]): number {
    if (this === window.CONTROLS?.tmpInpts && Array.isArray(args[0])) {
      const input = args[0] as Krunker.InputFrame;
      patchScrollInput(input);
      patchInputs(input);
    }
    return push.apply(this, args as any[]);
  };
}

const iInputs = {
  frame: 0,
  delta: 1,
  xDir: 2,
  yDir: 3,
  moveDir: 4,
  shoot: 5,
  scope: 6,
  jump: 7,
  crouch: 8,
  reload: 9,
  weaponScroll: 10,
  weaponSwap: 11,
} as const satisfies Record<string, keyof Krunker.InputFrame & number>;

type WheelCompatEvent = WheelEvent & {
  wheelDelta?: number;
  detail?: number;
};

const SCROLL_WHEEL_KEY_CODE = 20000;
let pendingWeaponScroll = 0;
let pendingWeaponScrollAt = 0;

function getControlsForScrollPatch(): Partial<Krunker.Controls> | null {
  return typeof CONTROLS === "undefined" ? null : CONTROLS || null;
}

function getWheelDelta(event: WheelCompatEvent): number {
  const rawDelta =
    typeof event.wheelDelta === "number"
      ? event.wheelDelta
      : typeof event.detail === "number" && event.detail
        ? -event.detail
        : -event.deltaY;
  return -Math.max(-1, Math.min(1, rawDelta || 0));
}

function canForceWheelWeaponSwap(
  controls: Partial<Krunker.Controls> | null,
): boolean {
  return (
    !!player?.active &&
    controls?.enabled === true &&
    controls.inputChanger == null &&
    !window.locked &&
    !window.spectating
  );
}

function clearWheelKeyState(controls: Partial<Krunker.Controls> | null): void {
  if (controls?.keys) delete controls.keys[SCROLL_WHEEL_KEY_CODE];
  if (controls?.didPressed) delete controls.didPressed[SCROLL_WHEEL_KEY_CODE];
}

function forceWheelWeaponSwap(delta: number): void {
  const controls = getControlsForScrollPatch();
  if (!delta || !canForceWheelWeaponSwap(controls)) return;

  controls.scrollToSwap = true;
  clearWheelKeyState(controls);
  pendingWeaponScroll = delta * (window.settings?.tmp?.scrollDirM ?? 1);
  pendingWeaponScrollAt =
    typeof performance === "undefined" ? Date.now() : performance.now();
}

function onWheelCapture(event: Event): void {
  forceWheelWeaponSwap(getWheelDelta(event as WheelCompatEvent));
}

function onWheelBubble(): void {
  const controls = getControlsForScrollPatch();
  if (pendingWeaponScroll) {
    if (controls) controls.scrollToSwap = true;
    clearWheelKeyState(controls);
  }
}

function installScrollWheelPatch(): void {
  const captureOptions: AddEventListenerOptions = {
    capture: true,
    passive: true,
  };
  const bubbleOptions: AddEventListenerOptions = {
    capture: false,
    passive: true,
  };
  window.addEventListener("wheel", onWheelCapture, captureOptions);
  window.addEventListener("mousewheel", onWheelCapture, captureOptions);
  window.addEventListener("DOMMouseScroll", onWheelCapture, captureOptions);
  window.addEventListener("wheel", onWheelBubble, bubbleOptions);
  window.addEventListener("mousewheel", onWheelBubble, bubbleOptions);
  window.addEventListener("DOMMouseScroll", onWheelBubble, bubbleOptions);
}

function patchScrollInput(inputs: Krunker.InputFrame): void {
  if (inputs[iInputs.weaponScroll] || !pendingWeaponScroll) return;
  const now =
    typeof performance === "undefined" ? Date.now() : performance.now();
  if (now - pendingWeaponScrollAt > 250) {
    pendingWeaponScroll = 0;
    return;
  }
  if (!canForceWheelWeaponSwap(getControlsForScrollPatch())) return;

  inputs[iInputs.weaponScroll] = pendingWeaponScroll;
  pendingWeaponScroll = 0;
}

installScrollWheelPatch();

let slideDidCrouch = false;
window.slideHold = false;

window.addEventListener("blur", (e) => {
  // bhop go boinggg
  // window.bhop = true;
  window.ctrlTp = false;
  window.slideHold = false;
});

window.addEventListener("keydown", (e) => {
  if (e.repeat || document.activeElement?.tagName === "INPUT") return;

  if (e.code === "Space") {
    window.bhop = true;
  } else if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
    window.slideHold = true;
  } else if (e.code === "ControlLeft" && window.ctrlTpEnabled) {
    window.ctrlTp = true;
  }
});

window.addEventListener("keyup", (e) => {
  // NOTE: do NOT gate on activeElement === INPUT here. Pressing Esc to
  // open the menu often shifts focus to a chat/search input, and the
  // resulting keyup for Space would then be swallowed — leaving
  // window.bhop stuck at true and the game auto-jumping in the menu.
  // Releasing held-key state must always be honored.
  if (e.repeat) return;

  if (e.code === "Space") {
    window.bhop = false;
  } else if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
    window.slideHold = false;
  } else if (e.code === "ControlLeft" && window.ctrlTpEnabled) {
    window.ctrlTp = false;
  }
});

// Safety net: when the pointer lock is released (Esc / menu open) the
// browser may also drop the in-flight keyup if focus jumped to an input
// before keyup arrived. Clear all held-key state to match.
document.addEventListener("pointerlockchange", () => {
  if (!document.pointerLockElement) {
    window.bhop = false;
    window.slideHold = false;
    window.ctrlTp = false;
  }
});

{
  const pr = EventTarget.prototype;
  const { addEventListener } = pr;
  pr.addEventListener = function (
    this: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ): void {
    if (
      listener &&
      this instanceof HTMLCanvasElement &&
      (type === "mousedown" || type === "mouseup")
    ) {
      const wrappedListener = function (this: EventTarget, event: Event): void {
        const mouseEvent = event as MouseEvent;
        // Call the original listener
        if (window.ctrlTpEnabled && window.ctrlTp && mouseEvent.which === 1) {
          if (event.type === "mouseup") {
            // window.ctrlTp = false;
          } else if (player?.active && RENDER?.camera) {
            const tpRaycaster = new window.THREE.Raycaster();
            tpRaycaster.far = 6000;

            tpRaycaster.setFromCamera({ x: 0, y: 0 }, RENDER.camera);
            const hits = tpRaycaster
              .intersectObject(RENDER.scene, true)
              .filter(function (h: any): boolean {
                let o = h.object;
                if (!o || !o.visible) return false;
                // walk up parents to skip local player
                let p = o;
                while (p) {
                  if (p === (player && player.object)) return false;
                  p = p.parent;
                }
                return true;
              });
            if (hits.length) {
              const pt = hits[0].point;
              io.send("tp", pt.x, pt.y + (player.height || 11), pt.z);
            }
          }
          return;
        }
        if (typeof listener === "function") listener.call(this, event);
        else listener.handleEvent(event);
      };
      return addEventListener.call(this, type, wrappedListener, options);
    }
    return addEventListener.call(this, type, listener, options);
  };
}

// zeroSome (-0.015..0.005) randomises the falling-velocity threshold
// at which we begin crouching for the next slide, so the slidehop
// cadence doesn't look frame-perfect.
function bhopRandom(min: number, max: number, isFloat = false): number {
  const v = Math.random() * (max - min) + min;
  return isFloat ? v : Math.floor(v);
}

function pickZeroSome(): number {
  return bhopRandom(-0.015, 0.005, true);
}

let bhopZeroSome = pickZeroSome();
let bhopNextZeroSome = pickZeroSome();
let bhopDidCrouch = false;
let bhopState = 0;
let rampBhopTimer = 0;
let rampBhopStart = 0;

const RAMP_BHOP_DELAY_MS = 20;
const RAMP_BHOP_DELAY_JITTER_MS = 10;

function resetRampBhopTimer(): void {
  rampBhopTimer = 0;
  rampBhopStart = 0;
}

type RuntimeRampObject = {
  active?: boolean;
  x?: number;
  y?: number;
  z?: number;
  width?: number;
  height?: number;
  length?: number;
  dir?: number;
  ramp?: {
    sX?: number;
    sZ?: number;
    eX?: number;
    eZ?: number;
  };
};

function finiteNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function collidesRampObject(lp: Krunker.Player, object: RuntimeRampObject): boolean {
  const scale = finiteNumber(lp.scale);
  const height = finiteNumber(lp.height);
  const x = finiteNumber(object.x);
  const y = finiteNumber(object.y);
  const z = finiteNumber(object.z);
  const width = finiteNumber(object.width);
  const objectHeight = finiteNumber(object.height);
  const length = finiteNumber(object.length);

  return (
    finiteNumber(lp.x) - scale < x + width &&
    finiteNumber(lp.x) + scale > x - width &&
    finiteNumber(lp.z) - scale < z + length &&
    finiteNumber(lp.z) + scale > z - length &&
    finiteNumber(lp.y) <= y + objectHeight &&
    finiteNumber(lp.y) + height >= y - objectHeight
  );
}

function getProgressOnLine(
  sX: number,
  sZ: number,
  eX: number,
  eZ: number,
  x: number,
  z: number,
): number {
  const progressOnLine = (UTILS as unknown as {
    progressOnLine?: (...args: number[]) => number;
  }).progressOnLine;
  const progress =
    typeof progressOnLine === "function"
      ? progressOnLine(sX, sZ, eX, eZ, x, z)
      : ((x - sX) * (eX - sX) + (z - sZ) * (eZ - sZ)) /
        ((eX - sX) ** 2 + (eZ - sZ) ** 2 || 1);
  return Math.max(0, Math.min(1, finiteNumber(progress)));
}

function getRampObjects(lp: Krunker.Player): RuntimeRampObject[] {
  if (typeof Game === "undefined" || !Game?.map?.manager) return [];
  const manager = Game.map.manager as Krunker.MapManager & {
    getGridArrays?: (x: number, y: number, z: number) => RuntimeRampObject[] | null;
  };
  if (typeof manager.getGridArrays === "function") {
    const nearby = manager.getGridArrays(
      finiteNumber(lp.x),
      finiteNumber(lp.y),
      finiteNumber(lp.z),
    );
    if (Array.isArray(nearby)) return nearby;
  }
  return Array.isArray(manager.objects)
    ? (manager.objects as RuntimeRampObject[])
    : [];
}

function isOnRampObject(lp: Krunker.Player): boolean {
  const wasOnGround = !!lp.onGround && !lp.didJump;
  for (const object of getRampObjects(lp)) {
    if (!object || object.active === false || !object.ramp) continue;
    if (!collidesRampObject(lp, object)) continue;

    const objectY = finiteNumber(object.y);
    const objectHeight = finiteNumber(object.height);
    if (finiteNumber(lp.y) >= objectY + objectHeight) continue;

    const dir = finiteNumber(object.dir);
    const progress = getProgressOnLine(
      finiteNumber(object.ramp.sX),
      finiteNumber(object.ramp.sZ),
      finiteNumber(object.ramp.eX),
      finiteNumber(object.ramp.eZ),
      finiteNumber(lp.x) + finiteNumber(lp.scale) * Math.cos(dir),
      finiteNumber(lp.z) + finiteNumber(lp.scale) * Math.sin(dir),
    );
    const rampY = objectY - objectHeight + (objectHeight * 2 + 0.01) * progress;
    if (finiteNumber(lp.y) <= rampY || wasOnGround) return true;
  }
  return false;
}

function isOnRamp(lp: Krunker.Player | undefined): boolean {
  return !!lp?.onGround && isOnRampObject(lp);
}

function patchRampBhopJump(inputs: Krunker.InputFrame): void {
  const now = Date.now();
  const frame = Number(inputs[iInputs.frame]) || 0;

  if (rampBhopTimer <= now) resetRampBhopTimer();

  if (!rampBhopTimer) {
    rampBhopTimer =
      now + RAMP_BHOP_DELAY_MS + bhopRandom(0, RAMP_BHOP_DELAY_JITTER_MS);
    rampBhopStart = frame + bhopRandom(1, 4);
    inputs[iInputs.jump] = 0;
    return;
  }

  inputs[iInputs.jump] = frame >= rampBhopStart && now < rampBhopTimer ? 1 : 0;
}

function patchInputs(inputs: Krunker.InputFrame): void {
  const lp = player;
  if (lp?.noclip) {
    bhopState = 0;
    resetRampBhopTimer();
    return;
  }
  const mode = window.bhopMode | 0;
  if (mode === BhopMode.DISABLED) {
    bhopState = 0;
    resetRampBhopTimer();
    slideDidCrouch = false;
    return;
  }

  // moveDir = -1 means idle; anything else is a movement direction
  const moving = inputs[iInputs.moveDir] !== -1;

  const jumpHeld = !!(inputs[iInputs.jump] || window.bhop);
  const crouchHeld = !!(inputs[iInputs.crouch] || window.slideHold);
  const autoBhop =
    (mode === BhopMode.SLIDEHOP || mode === BhopMode.ALWAYS_JUMP) &&
    moving;
  const holdBhop =
    (mode === BhopMode.HOLD_JUMP_CROUCH ||
      mode === BhopMode.HOLD_JUMP) &&
    jumpHeld;
  const wantBhop = autoBhop || holdBhop;

  if (wantBhop) {
    if (isOnRamp(lp)) {
      bhopState = 0;
      patchRampBhopJump(inputs);
    } else {
      resetRampBhopTimer();
      bhopState ^= 1;
      inputs[iInputs.jump] = bhopState;
    }
  } else {
    bhopState = 0;
    resetRampBhopTimer();
  }

  // Slidehop crouch logic: SLIDEHOP is automatic; HOLD_JUMP_CROUCH only runs
  // while crouch is held. HOLD_JUMP deliberately stays jump-only.
  const wantSlidehop =
    mode === BhopMode.SLIDEHOP ||
    (mode === BhopMode.HOLD_JUMP_CROUCH && crouchHeld);
  if (wantSlidehop) {
    const onGround = !!lp?.onGround;
    if (onGround && !inputs[iInputs.jump] && crouchHeld) {
      inputs[iInputs.crouch] = 1;
      bhopDidCrouch = true;
    } else {
      if (!bhopDidCrouch) bhopZeroSome = bhopNextZeroSome;
      const vy = (lp && (lp.velocity?.y ?? lp.velY)) || 0;
      const willCrouch = !onGround && vy < bhopZeroSome;
      inputs[iInputs.crouch] = willCrouch ? 1 : 0;
      if (!bhopDidCrouch && willCrouch) bhopNextZeroSome = pickZeroSome();
      bhopDidCrouch = willCrouch;
    }
    slideDidCrouch = bhopDidCrouch;
  } else {
    bhopDidCrouch = false;
    slideDidCrouch = false;
  }
}

window.addEventListener("blur", () => {
  const doHopsBoing = localStorage.boing;
  if (!doHopsBoing) window.bhop = false;
});
